// Centralized Analytics and Meta Pixel Tracking Utility

import { getAttribution } from "@/config/attribution";

// Must match the pixel id initialized in public/index.html. Used to re-init
// with Advanced Matching data once a lead's phone becomes known.
const PIXEL_ID = "356146746645877";

// Normalize a stored BD number (01XXXXXXXXX) to Meta's expected country-code
// form (8801XXXXXXXXX). Meta normalizes + SHA-256 hashes Advanced Matching
// fields client-side, so we deliberately pass the plain value (never store or
// log it from here) and let fbevents.js do the hashing.
const toMetaPhone = (localPhone) => {
  const digits = String(localPhone || "").replace(/\D/g, "");
  if (/^01\d{9}$/.test(digits)) return `880${digits.slice(1)}`;
  if (/^8801\d{9}$/.test(digits)) return digits;
  return null;
};

// Attach Advanced Matching so Meta can tie this Lead to a real FB/IG account —
// this is what makes phone-seeded Custom Audiences and Lookalikes accurate.
// Re-calling fbq('init') with user_data is Meta's documented way to add AM
// once the value is known (i.e. right after opt-in). No-op if fbq isn't ready.
const setAdvancedMatching = (phone) => {
  const ph = toMetaPhone(phone);
  if (!ph) return;
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("init", PIXEL_ID, { ph });
  }
};

export const fbPixel = (event, params = {}, eventId) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    // Pass eventID when present so this browser event dedupes against the
    // matching server-side (Conversions API) event sharing the same id.
    if (eventId) window.fbq('track', event, params, { eventID: eventId });
    else window.fbq('track', event, params);
  } else {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[Meta Pixel Log]: Event "${event}"`, params);
    }
  }
};

export const gaEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  } else {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[GA4 Log]: Event "${eventName}"`, params);
    }
  }
};

export const trackPageView = (toRoute) => {
  // Track Meta Pixel PageView
  fbPixel('PageView');

  // Track Google Analytics PageView
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('config', 'G-2Y43PXWPK3', {
      page_path: toRoute.path,
      page_title: toRoute.name || document.title
    });
  }
};

// ─────────────────────────────────────────────────────────────────────────
// Guideline lead-gen funnel events (GUIDELINES_FUNNEL_PLAN.md §4)
// Every event carries the persisted first-touch attribution so Meta/GA
// audiences and reports can segment by channel without per-link tagging.
// ─────────────────────────────────────────────────────────────────────────

const withAttribution = (params = {}) => ({ ...getAttribution(), ...params });

// Page load — top-funnel reminder audience.
export const trackGuidelineView = (subject) => {
  fbPixel('ViewContent', withAttribution({
    content_name: `BCS ${subject} Guideline`,
    content_category: 'BCS Study Guide',
    content_type: 'article',
  }));
  gaEvent('view_item', withAttribution({
    event_category: 'content',
    event_label: `bcs_${subject}_guideline`,
  }));
};

// Scrolled ~50% or 30s on page — engaged-but-not-opted-in audience.
export const trackViewedGuideline50 = (subject) => {
  fbPixel('ViewedGuideline50', withAttribution({ content_name: `BCS ${subject} Guideline` }));
  gaEvent('viewed_guideline_50', withAttribution({ event_label: `bcs_${subject}_guideline` }));
};

// Arm the ViewedGuideline50 trigger: fires once at 50% scroll OR 30s on page,
// whichever comes first. Call from each guideline page's onMounted.
export const armViewedGuideline50 = (subject) => {
  if (typeof window === 'undefined') return;
  let fired = false;
  let timer;
  const onScroll = () => {
    const reached = window.scrollY + window.innerHeight;
    const half = document.documentElement.scrollHeight * 0.5;
    if (reached >= half) fire();
  };
  const fire = () => {
    if (fired) return;
    fired = true;
    window.removeEventListener('scroll', onScroll);
    clearTimeout(timer);
    trackViewedGuideline50(subject);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  timer = setTimeout(fire, 30000);
};

// Opted in (phone or Messenger) — the primary KPI. lead mode only.
// `phone` (raw stored 01XXXXXXXXX) is optional: when present we set Meta
// Advanced Matching just before the Lead fires, so the conversion is matched
// to a real account and feeds high-quality Custom Audiences + Lookalikes.
// `eventId` (from saveLead) dedupes this browser event against the server-side
// Conversions API event that carries the same id.
export const trackLead = (subject, method, phone, eventId) => {
  if (phone) setAdvancedMatching(phone);
  fbPixel('Lead', withAttribution({
    content_name: `BCS ${subject} Guideline PDF`,
    content_category: 'Guideline Lead',
    method, // 'phone' | 'messenger'
  }), eventId);
  gaEvent('generate_lead', withAttribution({
    event_label: `bcs_${subject}_guideline`,
    method,
  }));
};

// Play-Store click — installers audience (exclude from install ads).
export const trackAppInstallClick = (subject) => {
  fbPixel('AppInstallClick', withAttribution({ content_name: `BCS ${subject} Guideline App` }));
  gaEvent('app_install_click', withAttribution({ event_label: `bcs_${subject}_guideline` }));
};

// Floating Messenger chat button click — support conversations are owned
// leads too. Uses Meta's standard 'Contact' event so it can seed audiences.
export const trackMessengerChatClick = (subject) => {
  fbPixel('Contact', withAttribution({
    content_name: `BCS ${subject} Guideline Chat`,
    content_category: 'Messenger Chat',
  }));
  gaEvent('messenger_chat_click', withAttribution({ event_label: `bcs_${subject}_guideline` }));
};

// Package page click — installed-but-not-bought audience.
export const trackInitiateCheckout = (subject) => {
  fbPixel('InitiateCheckout', withAttribution({
    content_name: 'BCS Package',
    content_category: 'Subscription',
    currency: 'BDT',
  }));
  gaEvent('begin_checkout', withAttribution({
    event_label: `bcs_${subject}_guideline_package`,
    currency: 'BDT',
  }));
};
